/**
 * Sheet modals:
 * - top (announcement Más info): opens sliding down; swipe up to close
 * - bottom (footer policies): opens sliding up; swipe down to close
 * Locks page scroll while open.
 */
(function () {
  const CLOSE_DISTANCE = 72;
  const CLOSE_VELOCITY = 0.45;

  function sheetContent(modal) {
    return modal.querySelector('.wod-sheet-modal__content, .announcement-bar-modal__content');
  }

  function placementOf(modal) {
    return modal.getAttribute('data-sheet-placement') === 'top' ? 'top' : 'bottom';
  }

  function syncScrollLock() {
    window.WodScrollLock?.sync?.();
  }

  function bindSheet(modal) {
    if (!(modal instanceof HTMLElement) || modal.dataset.wodSheetBound === '1') return;
    modal.dataset.wodSheetBound = '1';

    const content = sheetContent(modal);
    if (!content) return;

    const placement = placementOf(modal);
    const grabbers = Array.from(content.querySelectorAll('.wod-sheet-modal__grabber'));
    const header = content.querySelector('.wod-sheet-modal__header, .announcement-bar-modal__header');
    const dragTargets = [...grabbers, header].filter(Boolean);
    if (!dragTargets.length) return;

    let startY = 0;
    let startT = 0;
    let lastY = 0;
    let dragging = false;
    let currentY = 0;

    function setOffset(y) {
      currentY = y;
      content.style.transition = 'none';
      content.style.transform = `translate3d(0, ${currentY}px, 0)`;
    }

    function resetOffset() {
      content.style.transition = '';
      content.style.transform = '';
      content.style.opacity = '';
      currentY = 0;
    }

    function onStart(clientY) {
      dragging = true;
      startY = clientY;
      lastY = clientY;
      startT = performance.now();
      currentY = 0;
    }

    function onMove(clientY, event) {
      if (!dragging) return;
      const dy = clientY - startY;
      if (placement === 'bottom' && dy > 0) {
        event.preventDefault();
        setOffset(dy);
      } else if (placement === 'top' && dy < 0) {
        // Swipe up (bottom → top) dismisses the announcement sheet
        event.preventDefault();
        setOffset(dy);
      }
      lastY = clientY;
    }

    function onEnd() {
      if (!dragging) return;
      dragging = false;
      const elapsed = Math.max(16, performance.now() - startT);
      const velocity = (lastY - startY) / elapsed;
      const distance = Math.abs(currentY);
      const shouldClose =
        distance > CLOSE_DISTANCE ||
        (placement === 'bottom' && velocity > CLOSE_VELOCITY) ||
        (placement === 'top' && velocity < -CLOSE_VELOCITY);

      if (shouldClose && typeof modal.hide === 'function') {
        content.style.transition = 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)';
        content.style.transform =
          placement === 'top' ? 'translate3d(0, -110%, 0)' : 'translate3d(0, 110%, 0)';
        setTimeout(() => {
          modal.hide();
          resetOffset();
          syncScrollLock();
        }, 260);
      } else {
        content.style.transition = 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)';
        content.style.transform = 'translate3d(0, 0, 0)';
        setTimeout(() => resetOffset(), 280);
      }
    }

    dragTargets.forEach((el) => {
      el.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return;
          onStart(e.touches[0].clientY);
        },
        { passive: true }
      );
      el.addEventListener(
        'touchmove',
        (e) => {
          if (e.touches.length !== 1) return;
          onMove(e.touches[0].clientY, e);
        },
        { passive: false }
      );
      el.addEventListener('touchend', onEnd, { passive: true });
      el.addEventListener('touchcancel', onEnd, { passive: true });
    });

    modal.addEventListener('transitionend', (e) => {
      if (e.target !== modal && e.target !== content) return;
      if (!modal.hasAttribute('open')) resetOffset();
      syncScrollLock();
    });
  }

  function scan(root) {
    (root || document)
      .querySelectorAll('modal-dialog.wod-sheet-modal, modal-dialog.announcement-bar-modal')
      .forEach(bindSheet);
  }

  function patchModalDialog() {
    const Ctor = customElements.get('modal-dialog');
    if (!Ctor || Ctor.prototype.__wodSheetScrollPatched) return;
    const proto = Ctor.prototype;
    const show = proto.show;
    const hide = proto.hide;
    if (typeof show !== 'function' || typeof hide !== 'function') return;

    proto.show = function (...args) {
      const result = show.apply(this, args);
      if (this.matches?.('.wod-sheet-modal, .announcement-bar-modal')) {
        const content = sheetContent(this);
        // Drop any leftover inline transforms so CSS open/close transitions can run.
        if (content) {
          content.style.transition = '';
          content.style.transform = '';
          content.style.opacity = '';
        }
      }
      syncScrollLock();
      return result;
    };

    proto.hide = function (...args) {
      const result = hide.apply(this, args);
      if (this.matches?.('.wod-sheet-modal, .announcement-bar-modal')) {
        const content = sheetContent(this);
        if (content) {
          // After [open] is removed, drop inline overrides so the next open can slide in again.
          requestAnimationFrame(() => {
            content.style.transition = '';
            content.style.transform = '';
            content.style.opacity = '';
          });
        }
      }
      syncScrollLock();
      return result;
    };

    proto.__wodSheetScrollPatched = true;
  }

  function boot() {
    patchModalDialog();
    if (!customElements.get('modal-dialog')) {
      customElements.whenDefined('modal-dialog').then(patchModalDialog);
    }
    scan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => {
    patchModalDialog();
    scan(e.target);
  });

  // After modal close, Dawn restores focus to the opener and iOS paints a white ring.
  document.body.addEventListener('modalClosed', () => {
    syncScrollLock();
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return;
    if (
      active.matches('.announcement-bar__trigger, .wod3d-footer__policy-btn') ||
      active.closest('.announcement-bar__opener, .wod3d-footer__policy-opener')
    ) {
      active.blur();
    }
  });
})();
